import React, { useCallback, useEffect, useRef, useState } from "react";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { useTranslation } from "@me8eon/translation";
import { CategoryRow, SelectedCategory, fmt } from "../poData";
import CategoryQueriesPanel from "../components/CategoryQueriesPanel";
import TabSkeleton from "../components/TabSkeleton";
import TabError from "../components/TabError";
import { DashboardRange, fetchCategoryMatches } from "../../../services/dashboardApi";
import { useDashboardData } from "../useDashboardData";

// The search bar's match-count call is debounced independently of (and never mixed into) the
// main categories/KPI fetch above — it only ever updates the `matches` badge state below.
const MATCH_DEBOUNCE_MS = 300;

type Props = {
    initialCategory?: SelectedCategory | null;
    range: DashboardRange;
    periodDays: number;
    isCustomPeriod: boolean;
    categoryFilter: SelectedCategory[];
    companyFilter: string[];
};

const TotalQueriesTab: React.FC<Props> = ({ initialCategory = null, range, periodDays, isCustomPeriod, categoryFilter, companyFilter }) => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.totalQueries.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    const { kpis, categories, loading, error } = useDashboardData(range, categoryFilter, companyFilter);
    const maxQueries = Math.max(1, ...categories.map((c) => c.queries));
    const [selectedCat, setSelectedCat] = useState<SelectedCategory | null>(initialCategory);
    const [searchValue, setSearchValue] = useState("");
    const searchTerm = searchValue.trim();
    // Open the drill-down for a category (only live rows carry parent/child).
    const openCategory = (c: CategoryRow) => {
        if (c.parent && c.child) setSelectedCat({ parent: c.parent, child: c.child, label: c.category });
    };

    // Search-only match counts per category ("N matches" badge) — a separate, debounced call that
    // never touches `categories`/`kpis` above; keyed by "parent/child" so the breakdown table can
    // look a count up without any client-side re-aggregation.
    const idToken = useJwtToken();
    const { loggedIn } = useUserData();
    const [matches, setMatches] = useState<Record<string, number>>({});
    const rangeKey = "from" in range ? `r:${range.from}:${range.to}` : `w:${range.window}`;
    const categoriesKey = categoryFilter.map((c) => c.child).join(",");
    const companiesKey = companyFilter.join(",");
    const matchRequestIdRef = useRef(0);
    const latestMatchRef = useRef({ range, idToken, categoryFilter, companyFilter, loggedIn });
    latestMatchRef.current = { range, idToken, categoryFilter, companyFilter, loggedIn };

    const runSearchMatches = useCallback((term: string) => {
        const { range, idToken, categoryFilter, companyFilter, loggedIn } = latestMatchRef.current;
        if (!loggedIn) return;
        const requestId = ++matchRequestIdRef.current;
        const cats = categoryFilter.map((c) => c.child);
        const filters = { categories: cats.length ? cats : undefined, companies: companyFilter.length ? companyFilter : undefined };
        fetchCategoryMatches(range, term, idToken, filters)
            .then((list) => {
                if (matchRequestIdRef.current !== requestId) return; // superseded
                const map: Record<string, number> = {};
                list.forEach((m) => { if (m.parent && m.child) map[`${m.parent}/${m.child}`] = m.matches; });
                setMatches(map);
            })
            .catch(() => { if (matchRequestIdRef.current === requestId) setMatches({}); });
    }, [rangeKey, categoriesKey, companiesKey]);

    // Manually-scheduled (not the shared useDebouncedCallback hook) so clearing the search box can
    // explicitly cancel any still-pending timer — otherwise a stale fetch for the previous term
    // could fire after the box was cleared and silently repopulate `matches` in the background.
    const matchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (matchTimeoutRef.current) {
            clearTimeout(matchTimeoutRef.current);
            matchTimeoutRef.current = null;
        }
        if (!searchTerm) {
            setMatches({});
            return;
        }
        if (!loggedIn) return;
        matchTimeoutRef.current = setTimeout(() => runSearchMatches(searchTerm), MATCH_DEBOUNCE_MS);
        return () => {
            if (matchTimeoutRef.current) clearTimeout(matchTimeoutRef.current);
        };
    }, [searchTerm, loggedIn, rangeKey, categoriesKey, companiesKey, runSearchMatches]);

    if (loading) {
        return (
            <div className="po-queries">
                <TabSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="po-queries">
                <TabError />
            </div>
        );
    }

    // While searching, show the best-matching categories first; counts/share/bars stay untouched —
    // only the row order changes. No search term = original (query-count) order, unchanged.
    const displayCategories = searchTerm
        ? [...categories].sort((a, b) => {
              const aKey = a.parent && a.child ? `${a.parent}/${a.child}` : null;
              const bKey = b.parent && b.child ? `${b.parent}/${b.child}` : null;
              const aMatches = aKey ? matches[aKey] ?? 0 : 0;
              const bMatches = bKey ? matches[bKey] ?? 0 : 0;
              return bMatches - aMatches;
          })
        : categories;

    return (
        <div className="po-queries">
            <div className="po-section-head">
                <div>
                    <h2>{tp("title")}</h2>
                    <p className="po-section-head__sub">
                        {fmt(kpis.totalQueries)} {tp("queries")} · {kpis.categories} {tc("categories")} · {tc(isCustomPeriod ? "for" : "last")} {periodDays} {tc(periodDays === 1 ? "day" : "days")}
                    </p>
                </div>
                {/* Trend delta hidden for now */}
                {/* <span className="po-trend po-trend--up po-trend--lg">
                    {kpis.totalQueriesDelta == null ? "—" : `↗ +${kpis.totalQueriesDelta}% ${tp("vsPrior")}`}
                </span> */}
                <div className="po-search">
                    <EonUiIcon name="search" size="small" className="po-search__icon" />
                    <input
                        type="search"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder={tp("searchPlaceholder")}
                    />
                </div>
            </div>

            {/* Category breakdown table */}
            <div className="po-card po-table-card">
                <div className="po-card__head">
                    <h3>{tp("breakdownTitle")}</h3>
                    <span className="po-card__meta">{kpis.categories} {tc("categories")}</span>
                </div>
                <table className="po-table">
                    <thead>
                        <tr>
                            <th>{tp("thCategory")}</th>
                            <th className="num">{tp("thQueries")}</th>
                            <th className="num">{tp("thShare")}</th>
                            <th>{tp("thDistribution")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayCategories.map((c, i) => {
                            const matchKey = c.parent && c.child ? `${c.parent}/${c.child}` : null;
                            const matchCount = matchKey ? matches[matchKey] ?? 0 : 0;
                            return (
                                <tr
                                    key={c.parent && c.child ? `${c.parent}/${c.child}` : `${c.category}-${i}`}
                                    className={c.parent && c.child ? "po-row--clickable" : ""}
                                    onClick={() => openCategory(c)}
                                >
                                    <td className="po-table__strong">
                                        {c.category}{" "}
                                        {searchTerm && matchCount > 0 && (
                                            <span className="po-badge po-badge--match">
                                                {matchCount} {tp(matchCount === 1 ? "matchSuffix" : "matchesSuffix")}
                                            </span>
                                        )}
                                    </td>
                                    <td className="num">{fmt(c.queries)}</td>
                                    <td className="num po-table__muted">{c.share}%</td>
                                    <td>
                                        <div className="po-minibar">
                                            <div
                                                className="po-minibar__fill"
                                                style={{ width: `${(c.queries / maxQueries) * 100}%` }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Drill-down: individual queries for the clicked category */}
            {selectedCat && (
                <CategoryQueriesPanel
                    parent={selectedCat.parent}
                    child={selectedCat.child}
                    label={selectedCat.label}
                    range={range}
                    companies={companyFilter}
                    search={searchTerm || undefined}
                    onClose={() => setSelectedCat(null)}
                />
            )}
        </div>
    );
};

export default TotalQueriesTab;
